// Client-side Bitcoin address validation for immediate payout-form feedback.
// A dependency-free port of backend/pools/validators.py: full bech32/bech32m
// checksum validation (the common bc1…/tb1… case) and structural base58
// validation (charset + 25-byte decode + version byte). The base58 double-SHA256
// checksum is verified server-side (we don't pull in a hash dependency for it),
// so the server stays the source of truth; this just rejects obvious garbage and
// typos before the round trip.
//
// Written without BigInt or string iteration so it compiles at the project's
// pre-ES2020 target.

const BASE58_VERSIONS: Record<string, Set<number>> = {
  mainnet: new Set([0x00, 0x05]),
  testnet: new Set([0x6f, 0xc4]),
  regtest: new Set([0x6f, 0xc4]),
};

const BECH32_HRP: Record<string, string> = { mainnet: 'bc', testnet: 'tb', regtest: 'bcrt' };

// --- base58 (structural; checksum verified server-side) ---------------------
const B58 = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

const base58Version = (addr: string): number | null => {
  const bytes: number[] = []; // big-endian base-256 accumulator

  for (let i = 0; i < addr.length; i += 1) {
    const value = B58.indexOf(addr.charAt(i));

    if (value < 0) {
      return null;
    }

    let carry = value;

    for (let j = bytes.length - 1; j >= 0; j -= 1) {
      carry += bytes[j] * 58;
      bytes[j] = carry & 0xff;
      carry >>= 8;
    }
    while (carry > 0) {
      bytes.unshift(carry & 0xff);
      carry >>= 8;
    }
  }

  // Each leading '1' is a leading zero byte of the decoded payload.
  let leading = 0;

  while (leading < addr.length && addr.charAt(leading) === '1') {
    leading += 1;
  }

  const full = [...new Array(leading).fill(0), ...bytes];

  return full.length === 25 ? full[0] : null;
};

// --- bech32 / bech32m (BIP173 / BIP350 reference) ---------------------------
const CHARSET = 'qpzry9x8gf2tvdw0s3jn54khce6mua7l';

const polymod = (values: number[]): number => {
  const gen = [0x3b6a57b2, 0x26508e6d, 0x1ea119fa, 0x3d4233dd, 0x2a1462b3];
  let chk = 1;

  for (let i = 0; i < values.length; i += 1) {
    const top = chk >> 25;

    chk = ((chk & 0x1ffffff) << 5) ^ values[i];
    for (let k = 0; k < 5; k += 1) {
      chk ^= ((top >> k) & 1) ? gen[k] : 0;
    }
  }

  return chk >>> 0;
};

const hrpExpand = (hrp: string): number[] => {
  const high: number[] = [];
  const low: number[] = [];

  for (let i = 0; i < hrp.length; i += 1) {
    high.push(hrp.charCodeAt(i) >> 5);
    low.push(hrp.charCodeAt(i) & 31);
  }

  return [...high, 0, ...low];
};

const verifyChecksum = (hrp: string, data: number[]): string | null => {
  const value = polymod([...hrpExpand(hrp), ...data]);

  if (value === 1) {
    return 'bech32';
  }
  if (value === 0x2bc830a3) {
    return 'bech32m';
  }

  return null;
};

const bech32Decode = (bech: string): { hrp: string, data: number[], spec: string } | null => {
  for (let i = 0; i < bech.length; i += 1) {
    const code = bech.charCodeAt(i);

    if (code < 33 || code > 126) {
      return null;
    }
  }
  if (bech.toLowerCase() !== bech && bech.toUpperCase() !== bech) {
    return null;
  }

  const lower = bech.toLowerCase();
  const pos = lower.lastIndexOf('1');

  if (pos < 1 || pos + 7 > lower.length || lower.length > 90) {
    return null;
  }

  const data: number[] = [];

  for (let i = pos + 1; i < lower.length; i += 1) {
    const idx = CHARSET.indexOf(lower.charAt(i));

    if (idx < 0) {
      return null;
    }
    data.push(idx);
  }

  const hrp = lower.slice(0, pos);
  const spec = verifyChecksum(hrp, data);

  return spec ? { hrp, data: data.slice(0, -6), spec } : null;
};

const convertbits = (data: number[], from: number, to: number): number[] | null => {
  let acc = 0;
  let bits = 0;
  const ret: number[] = [];
  const maxv = (1 << to) - 1;
  const maxAcc = (1 << (from + to - 1)) - 1;

  for (let i = 0; i < data.length; i += 1) {
    const value = data[i];

    if (value < 0 || (value >> from)) {
      return null;
    }
    acc = ((acc << from) | value) & maxAcc;
    bits += from;
    while (bits >= to) {
      bits -= to;
      ret.push((acc >> bits) & maxv);
    }
  }

  // pad=false (witness program): no leftover bits, remainder must be zero.
  if (bits >= from || ((acc << (to - bits)) & maxv)) {
    return null;
  }

  return ret;
};

const isValidSegwit = (hrp: string, addr: string): boolean => {
  const decoded = bech32Decode(addr);

  if (!decoded || decoded.hrp !== hrp || decoded.data.length < 1) {
    return false;
  }

  const program = convertbits(decoded.data.slice(1), 5, 8);

  if (!program || program.length < 2 || program.length > 40) {
    return false;
  }

  const witver = decoded.data[0];

  if (witver > 16) {
    return false;
  }
  if (witver === 0 && program.length !== 20 && program.length !== 32) {
    return false;
  }
  if (witver === 0 && decoded.spec !== 'bech32') {
    return false;
  }
  if (witver !== 0 && decoded.spec !== 'bech32m') {
    return false;
  }

  return true;
};

export const isValidBitcoinAddress = (address: string, network = 'mainnet'): boolean => {
  if (!address || typeof address !== 'string') {
    return false;
  }

  const hrp = BECH32_HRP[network];

  if (hrp && address.toLowerCase().startsWith(`${hrp}1`)) {
    return isValidSegwit(hrp, address);
  }

  const version = base58Version(address);

  return version !== null && (BASE58_VERSIONS[network]?.has(version) ?? false);
};

// The client doesn't know the configured network, so accept an address valid on
// any network; the server enforces the exact network for this deployment.
export const isPlausibleBitcoinAddress = (address: string): boolean =>
  isValidBitcoinAddress(address, 'mainnet')
  || isValidBitcoinAddress(address, 'testnet')
  || isValidBitcoinAddress(address, 'regtest');

export default isValidBitcoinAddress;
